import { Box, Container, Tab, Tabs, Typography } from "@mui/material";
import Heading from "../../common/Heading";
import { memo, useCallback, useState } from "react";
import useAccount from "../../../hooks/useAccount";
import CreateFTRWorkflow from "./CreateFTRWorkflow";
import FTRLodgement from "./FTRLodgement";
import ListApplications from "./ListApplications";
import Account from "../../../models/Account";
import ConnectedAccount from "../../../models/ConnectedAccount";
import FTRQuestionnaire from "./FTRQuestionnaire";
import FTRDisruptiveControls from "./FTRDisruptiveControls";
import FTRGenerateReports from "./FTRGenerateReports";
import FTRNonDisruptiveCloudWatchRemediate from "./FTRNonDisruptiveCloudWatchRemediate";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

type AnswerQuestion = {
  id: string;
  selection: string;
};

const TabPanel = memo(({ children, value, index, ...other }: TabPanelProps) => {
  return (
    <Container
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="span">{children}</Typography>
        </Box>
      )}
    </Container>
  );
});

/**
 * Used for tabs
 */
function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
/**
 * Main Page of FTR Workflow
 */
export default function FTRWorkflow() {
	const { arn, accounts, accounttype } = useAccount();
	const [value, setValue] = useState(0);
	const [applicationName, setApplicationName] = useState("");
	const [targetAccountType, setTargetAccountType] = useState(accounttype);
	const [technicalReviewArn, setTechnicalReviewArn] = useState("");
	const [connectedAccounts, setConnectedAccounts] = useState<Array<ConnectedAccount>>([]);
	const [answers, setAnswers] = useState<Array<AnswerQuestion>>([]);
	const [selectedControls, setSelectedControls] = useState<any[]>([]);
	const [unlockSteps, setUnlockSteps] = useState({
		step2: false,
		step3: false,
		step4: false,
		step5: false,
	});

	// Handles which tab to change to
	const handleChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	}, [setValue]);

	// Handle answer changes
  const handleAnswerChange = useCallback((questionId: string, newAnswer: string) => {
    setAnswers((prev) => {
      const index = prev.findIndex((ans) => ans.id === questionId);
      if (index !== -1) {
        // Update existing answer
        const updatedAnswers = [...prev];
        updatedAnswers[index].selection = newAnswer;
        return updatedAnswers;
      } else {
        // Add new answer
        return [...prev, { id: questionId, selection: newAnswer}];
      }
    });
  }, []);

	// Function to update selected controls
  const updateSelectedControls = useCallback((control: any, isSelected: boolean) => {
    setSelectedControls((prev) =>
      isSelected
        ? prev.some((c) => c.controlname === control.controlname)
          ? prev
          : [...prev, control]
        : prev.filter((c) => c.controlname !== control.controlname)
    );
  }, []);

	// Gets triggered when list application tab is loaded
	const resetStates = useCallback(() => {
		setApplicationName("");
		setTechnicalReviewArn("");
		setTargetAccountType("");
		setConnectedAccounts([]);  
	}, []);

	// Gets triggered when update button or report button is clicked from list applications page
	const updateApplication = useCallback((
		applicationName: string,
		awsTechnicalReviewAccount: string,
		accountIdRef: string,
		step: string,
	) => {
		setApplicationName(applicationName);
		setTechnicalReviewArn(awsTechnicalReviewAccount);

		const account = accounts.find((i: Account) => i.arn === awsTechnicalReviewAccount);

		if(account) {
			if(account?.connectedAccounts.length > 1){
				//Get filtered connected accounts
				const getFilteredConnectedAccounts = () => {
					return (
						account?.connectedAccounts
							?.filter((connectedAccount: ConnectedAccount) => {
								return connectedAccount.accountIdRef === accountIdRef;
							})
							.map((connectedAccount: ConnectedAccount) => {
								return {
									accountIdRef: connectedAccount.accountIdRef,
									region: connectedAccount.region,
								};
							}) || []
					);
				};
				setConnectedAccounts(getFilteredConnectedAccounts());  
			} else {
				setConnectedAccounts([]);  
			} 
			setTargetAccountType(account?.accounttype);
		}
		if(step === "UPDATE"){
			//Swap to step 2
			setUnlockSteps((prevState) => ({
				...prevState,
				step2: true, // Unlock step 2
			}));
			setValue(1);
		} else {
			//Swap to step 4
			setUnlockSteps((prevState) => ({
				...prevState,
				step5: true, // Unlock step 5
			}));
			setValue(4);
		}
	}, [ accounts ]);

	const tabPanels = [
		{
			index: 0,
			component: (
				<CreateFTRWorkflow
					arn={arn}
					targetAccountType={targetAccountType}
					setUnlockSteps={setUnlockSteps}
					changeTab={useCallback((index: number, applicationName: string, targetArn: string, connectedAccounts: Array<ConnectedAccount>, accounttype: string) => {
						setValue(index);
						setApplicationName(applicationName);
						setTechnicalReviewArn(targetArn);
						setConnectedAccounts(connectedAccounts);
						setTargetAccountType(accounttype);
					},[
						setValue,
						setApplicationName,
						setTechnicalReviewArn,
						setConnectedAccounts,
						setTargetAccountType
					])}
				/>
			),
		},
		{
			index: 1,
			component: (
				<FTRNonDisruptiveCloudWatchRemediate
					targetAccountType={targetAccountType}
          technicalReviewArn={technicalReviewArn}
          connectedAccounts={connectedAccounts}
          selectedControls={selectedControls} // Pass the centralized state
          updateSelectedControls={updateSelectedControls} // Pass the updater function
					setUnlockSteps={setUnlockSteps}
					changeTab={useCallback((index: number) => setValue(index),[setValue])}
				/>
			),
		},
		{
			index: 2,
			component: (
				<FTRQuestionnaire
					technicalReviewArn={technicalReviewArn}
					applicationName={applicationName}
					answers={answers}
					onAnswerChange={handleAnswerChange}
					setUnlockSteps={setUnlockSteps}
					changeTab={useCallback((index: number) => setValue(index),[setValue])}
				/>
			),
		},
		{
			index: 3,
			component: (
				<FTRDisruptiveControls
					technicalReviewArn={technicalReviewArn}
          connectedAccounts={connectedAccounts}
					setUnlockSteps={setUnlockSteps}
					changeTab={useCallback((index: number) => setValue(index),[setValue])}
				/>
			),
		},
		{
			index: 4,
			component: (
				<FTRGenerateReports
					technicalReviewArn={technicalReviewArn}
					applicationName={applicationName}
          connectedAccounts={connectedAccounts}
					setUnlockSteps={setUnlockSteps}
					changeTab={useCallback((index: number) => setValue(index),[setValue])}
				/>
			),
		},
		{
			index: 5,
			component: (
				<FTRLodgement/>
			),
		},
		{
			index: 6,
			component: (
				<ListApplications
					arn={arn}
					updateApplication={updateApplication}
					resetStates={resetStates}
					setUnlockSteps={setUnlockSteps}
				/>
			),
		},
	];

	return (
		<Container component="main" maxWidth="lg">
			<Heading heading={"Foundational Technical Review Automation"} />
			<Box sx={{ width: "100%" }}>
				<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
					<Tabs
						value={value}
						onChange={handleChange}
						textColor="secondary"
						indicatorColor="secondary"
						variant="fullWidth"
					>
						<Tab label="STEP 1: Create" {...a11yProps(0)} />
						<Tab
							label="STEP 2: Non-Disruptive Recommendations"
							disabled={unlockSteps.step2 === false}
							{...a11yProps(1)}
							sx={{
                "&.Mui-disabled": {
                  color: "#c0c0c0"
                }
              }}
						/>
						<Tab 
							label="STEP 3: People, Policy & Tools Review"
							disabled={unlockSteps.step3 === false}
							{...a11yProps(2)}
							sx={{
                "&.Mui-disabled": {
                  color: "#c0c0c0"
                }
              }}
						/>
						<Tab 
							label="STEP 4: Remediate Remaining Controls"
							disabled={unlockSteps.step4 === false}
							{...a11yProps(3)} 
							sx={{
                "&.Mui-disabled": {
                  color: "#c0c0c0"
                }
              }}
						/>
						<Tab 
							label="STEP 5: Generate Reports"
							disabled={unlockSteps.step5 === false}
							{...a11yProps(4)} 
							sx={{
                "&.Mui-disabled": {
                  color: "#c0c0c0"
                }
              }}
						/>
						<Tab
							label="FTR lodgement instructions"
							{...a11yProps(5)}
						/>
						<Tab
							label="Review Applications"
							{...a11yProps(6)}
						/>
					</Tabs>
				</Box>
				{tabPanels.map(({ index, component }) => (
					<TabPanel value={value} index={index} key={`MultiFramework-${index}`}>
						{component}
					</TabPanel>
				))}
			</Box>
		</Container>
	)
}